import React, { FC, useEffect } from 'react';
import { RouteComponentProps, useHistory, withRouter } from 'react-router';
import styled from '@emotion/styled';
import { useDeviceInfo, useI18n, useLicenses, useLogger, useMetadata, useSelector } from '~/Hooks';
import DemoIntro from '~Scenes/DemoIntro';
import { CompanyModules } from './types';
import { Box } from '~Styles';
import Heading from '~ComponentsImproved/Typography/Heading';
import LoginModal from '~Scenes/Tables/Components/LoginModal';
import bLogo from '~Assets/Logos/b_logo_black.svg';
import { GradientBlur, Logo, LogoContainer, ScrollWrapper, Wrapper } from './styles';
import useModal from '~ComponentsImproved/Modal/useModal';
import Modal from '~ComponentsImproved/Modal';
import ModalWrapper from '~ComponentsImproved/Modal/ModalWrapper';
import ClaimDeviceV2 from '~Components/ClaimDevice';
import ClaimDevice from '~Scenes/Onboarding/Components/ClaimDevice';
import NewsAndUpdates from './Components/NewsAndUpdates';
import { useWindowSize } from '~/Hooks';
import ReleasesButton from './Components/ReleasesButton';
import ModesContent from './Components/NewsAndUpdates/ModesContent';

type Props = RouteComponentProps;

const Gateway: FC<Props> = (props) => {
  const { i18n } = useI18n();
  const licenses = useLicenses();
  const deviceInfo = useDeviceInfo();
  const logger = useLogger('gateway');
  const history = useHistory();
  const windowSize = useWindowSize();
  const metadata = useMetadata();

  const changeDeviceModal = useModal();

  const demoMode = useSelector((state) => state.demoMode);
  const staffId = useSelector((state) => state.staff?.id);
  const companyFlags = useSelector((state) => state.companyFlags);
  const [, , venueId] = props.location.pathname.split('/');

  const companyModules: CompanyModules = {
    POS: { active: licenses.hasQSR, claimable: licenses.isQSRClaimable, path: 'pos' },
    tables: { active: licenses.hasTSR, claimable: licenses.isTSRClaimable, path: 'tables' },
    appOrders: { active: licenses.hasApp, claimable: false, path: 'orders' },
    KDS: { active: licenses.hasKDS, claimable: licenses.isKDSClaimable, path: 'kitchen-display' },
    CDS: {
      active: licenses.hasCustomerDisplay,
      claimable: licenses.isCustomerDisplayClaimable,
      path: 'customer-display',
    },
    express: {
      active: licenses.hasEPOS,
      claimable: licenses.isEPOSClaimable,
      path: 'self-service-kiosks',
    },
    // EPOS 2.0 runs on the same license as the existing express kiosk.
    epos2: {
      active: licenses.hasEPOS,
      claimable: licenses.isEPOSClaimable,
      path: 'express',
    },
    inventory: { active: licenses.hasInventory, claimable: false, path: 'inventory' },
  };

  const activeModules = Object.keys(companyModules).filter(
    (cm) =>
      companyModules[cm as keyof CompanyModules].active ||
      companyModules[cm as keyof CompanyModules].claimable,
  );

  /**
   * Redirect to handy flow if it's a handheld terminal or to customer display if that is the only license available.
   */
  useEffect(() => {
    logger.info('checking gateway');

    const isUsingBrowser = !window.ReactNativeWebView;

    if (companyFlags.useNewLicenses && companyModules.CDS.active && isUsingBrowser) {
      logger.info('pushing to customer display flow');
      history.push(`/venue/${venueId}/customer-display-responsive`);
      return;
    }

    if (!deviceInfo) {
      logger.info('No device info available yet');
      return;
    }

    logger.info('Model: ' + deviceInfo.model);
    logger.info('Is handheld terminal: ' + deviceInfo.isHandheldTerminal);

    if (deviceInfo.isHandheldTerminal) {
      logger.info('pushing to handy flow');
      history.push(`/venue/${venueId}/handy`);
    }
  }, [deviceInfo]);

  const noLicensesAvailable =
    !licenses.loading && companyFlags.useNewLicenses && activeModules.length === 0;

  const showReleasesInModal = windowSize.width < 1168;

  if (demoMode) {
    return <DemoIntro />;
  }

  return (
    <>
      <Wrapper>
        <GradientBlur aria-hidden="true" />
        <ScrollWrapper>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            marginTop="medium"
            fluid
            style={{ textAlign: noLicensesAvailable ? 'center' : undefined }}>
            <Box display="flex" flexDirection="column" gap="xs" fluid>
              <Heading level={1} color={'var(--colorWhite)'}>
                {metadata.data.venueName ?? i18n('Menu.Home')}
              </Heading>
              <Heading level={4} color={'var(--colorBlueGray200)'}>
                {i18n('General.ChooseMode')}
              </Heading>
            </Box>

            {showReleasesInModal ? (
              <ReleasesButton />
            ) : (
              !noLicensesAvailable && (
                <LogoContainer>
                  <Logo src={bLogo} alt="Baemingo Logo" />
                </LogoContainer>
              )
            )}
          </Box>

          <Box display="flex" justifyContent="space-between" gap="medium">
            <ModesContent
              companyModules={companyModules}
              noLicensesAvailable={noLicensesAvailable}
              venueId={venueId}
              toggleChangeDeviceModal={() => changeDeviceModal.toggle()}
            />

            {!noLicensesAvailable && !showReleasesInModal && <NewsAndUpdates />}
          </Box>
        </ScrollWrapper>
      </Wrapper>

      {!staffId && <LoginModal />}

      <Modal isShowing={changeDeviceModal.isShowing} hide={() => changeDeviceModal.toggle()}>
        <ModalWrapper>
          {companyFlags.useNewLicenses ? (
            <ClaimDeviceV2
              isChangeOfDevice
              onComplete={() => {
                logger.info('Device changed in Gateway');
                changeDeviceModal.toggle();
              }}
            />
          ) : (
            <ClaimDevice
              onComplete={() => {
                logger.info('Device changed in Gateway');
                changeDeviceModal.toggle();
              }}
            />
          )}
        </ModalWrapper>
      </Modal>
    </>
  );
};

export default withRouter(Gateway);
